import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  ErrorLabel,
  Input,
  ScrollArea,
  Switch,
  Textarea,
} from '@ibsys/kit';
import { ArchiveRestoreIcon } from 'lucide-react';
import { FC, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ATTRIBUTE_TYPES, AttributeResponse, useDeleteAttribute, useRestoreAttribute } from '@/entities/attributes';
import { ConfirmationButton } from '@/shared/ui/ConfirmationButton/ConfirmationButton';
import { AiInput, EditableTitle, SaveButton } from '@/shared/ui';
import { AttributeFormSelectValue } from './AttributeFormSelectValues';
import { AttributeGroupsField } from './AttributeGroupsField';
import { RuleField } from './RuleField';
import { StructureForm } from './StructureForm';
import { useAttributeForm } from './useAttributeForm';
import { useGetAttributeKinds, useGetAttributeTypes, useGetDisplayFormats } from '../../utils';
import { useAppNavigate } from '@/shared/lib';
import { useFormGuardActions } from '@/entities/formGuard';

type Props = {
  initial?: AttributeResponse;
  onSuccess?: () => void;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
  searchParams?: URLSearchParams;
};

export const AttributeForm: FC<Props> = ({ initial, onSuccess, onUnsavedChange }) => {
  const { t } = useTranslation(['common', 'attributes']);
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const { options: kindOptions } = useGetAttributeKinds();

  const { resetFormGuard } = useFormGuardActions();

  // Use searchParams from props if provided, otherwise use from hook

  const {
    form,
    isEdit,
    isLoading,
    isSelectable,
    isStructure,
    currentType,
    isDisplayFormatSupported,
    isDataTypeEditable,
    isListEditable,
    onSubmit,
    isDisabled,
    canRestore,
    handleTitleSave,
    handleNameBlur,
    isCodeLocked,
    isGeneratingCode,
  } = useAttributeForm({
    initial,
    onSuccess,
    searchParams,
  });

  // У атрибута-структуры вариант «Структура» остаётся в списке, чтобы поле показало текущий тип,
  // хотя выбор в нём и закрыт
  const { options: typeOptions } = useGetAttributeTypes({
    withStructure: !isEdit || initial?.dataType === ATTRIBUTE_TYPES.STRUCTURE,
  });
  // Набор форматов отображения зависит от выбранного типа данных
  const { options: displayFormatOptions } = useGetDisplayFormats(currentType);

  const { mutateAsync: deleteAttribute } = useDeleteAttribute();
  const { mutateAsync: restoreAttribute, isPending: isRestoring } = useRestoreAttribute();

  const { control } = form;
  const { formState } = form;

  useEffect(() => {
    onUnsavedChange?.(formState.isDirty);
  }, [formState.isDirty, onUnsavedChange]);

  const handleDelete = async () => {
    if (initial) {
      await deleteAttribute({ code: initial.code });
      resetFormGuard();
      navigate(`/attributes`, {}, { replace: true, saveParams: true });
    }
  };

  const handleRestore = async () => {
    if (initial) {
      await restoreAttribute({ code: initial.code });
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b flex gap-3 flex-wrap items-center justify-between">
        {isEdit && !isDisabled ? (
          <EditableTitle
            value={form.watch('name')}
            label={t('attributes:renameAttribute')}
            onSave={handleTitleSave}
            disabled={isDisabled}
            placeholder={t('attributes:editAttribute')}
            textClassName="text-xl font-semibold"
          />
        ) : (
          <CardTitle className={'flex gap-2 items-center'}>
            {t('attributes:addAttribute')}
            {initial?.type === 'SYSTEM' && (
              <Badge variant={'primary'} outline>
                {t('attributes:system')}
              </Badge>
            )}
            {initial?.isDeleted && (
              <Badge variant={'destructive'} outline>
                {t('attributes:deleted')}
              </Badge>
            )}
          </CardTitle>
        )}
        {!isDisabled && (
          <SaveButton
            className="ml-auto"
            isDirty={formState.isDirty}
            isLoading={isLoading}
            onClick={onSubmit}
            secondaryAction={
              isEdit && (
                <ConfirmationButton
                  label={t('attributes:deleteAttribute')}
                  onConfirm={handleDelete}
                  title={t('attributes:removeAttributeTitle')}
                  description={<Trans>{t('attributes:removeAttributeDescription', { name: initial?.name })}</Trans>}
                />
              )
            }
          />
        )}
        {canRestore && (
          <ConfirmationButton
            className="ml-auto"
            onConfirm={handleRestore}
            isLoadingConfirm={isRestoring}
            title={t('attributes:restoreAttributeTitle')}
            description={<Trans>{t('attributes:restoreAttributeDescription', { name: initial?.name })}</Trans>}
            trigger={{
              icon: <ArchiveRestoreIcon />,
              variant: 'secondary',
              color: 'primary',
              children: t('attributes:restore'),
            }}
          />
        )}
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0">
        <FormProvider {...form}>
          <ScrollArea className="h-full">
            <div className="flex flex-col gap-4 p-2 md:p-6 max-w-3xl">
              {!isEdit && (
                <AiInput
                  control={control}
                  name="name"
                  label={t('attributes:name')}
                  required
                  isProcessing={isGeneratingCode}
                  disabled={isDisabled}
                  onBlur={handleNameBlur}
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  control={control}
                  name="code"
                  label={t('attributes:code')}
                  required
                  disabled={isEdit || isDisabled || isCodeLocked}
                  placeholder={isCodeLocked ? t('common:nameIsTheFirst') : undefined}
                />
                <Combobox
                  control={control}
                  name="dataType"
                  label={t('attributes:dataType')}
                  required
                  disabled={!isDataTypeEditable}
                  options={typeOptions}
                />
                <Switch control={control} name="isList" label={t('attributes:isList')} disabled={!isListEditable} />

                {isDisplayFormatSupported && (
                  <Combobox
                    control={control}
                    name="displayFormat"
                    label={t('attributes:displayFormat.title')}
                    disabled={isDisabled}
                    options={displayFormatOptions}
                  />
                )}

                <Combobox
                  control={control}
                  name="type"
                  label={t('attributes:attributeKind.title')}
                  disabled={isDisabled}
                  options={kindOptions}
                  className="col-span-2"
                />

                <RuleField
                  name="rule"
                  rows={3}
                  disabled={isDisabled}
                  inputClassName="max-h-[200px]"
                  className="col-span-2"
                />

                <Textarea
                  control={control}
                  name="description"
                  label={t('attributes:description')}
                  rows={4}
                  required
                  disabled={isDisabled}
                  classNames={{ input: 'max-h-[200px]' }}
                  className="sm:col-span-2"
                />

                <Textarea
                  control={control}
                  name="tooltip"
                  label={t('attributes:tooltip')}
                  rows={3}
                  disabled={isDisabled}
                  classNames={{ input: 'max-h-[200px]' }}
                  className="sm:col-span-2"
                />
              </div>

              <AttributeGroupsField disabled={isDisabled} />

              {isSelectable && <AttributeFormSelectValue className="my-4" disabled={isDisabled} />}

              {isStructure && (
                <>
                  <StructureForm disabled={isDisabled} />
                  {form.formState.errors.structure && (
                    <ErrorLabel className="mt-2">{form.formState.errors.structure.message}</ErrorLabel>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
